/**
 * LocalCache - Sistema de caché local agresiva para optimizar rendimiento
 * Almacena datos en localStorage con TTL configurable
 */

import { logger } from './logger';


const CACHE_PREFIX = 'stock_cache_';
const DEFAULT_TTL = 2 * 60 * 1000; // 2 minutos por defecto

// Cache en memoria para acceso más rápido
const memoryCache = new Map();

/**
 * Guarda un valor en caché con TTL
 */
export const setCache = (key, value, ttlMs = DEFAULT_TTL) => {
  const cacheKey = `${CACHE_PREFIX}${key}`;
  const cacheEntry = {
    value,
    timestamp: Date.now(),
    ttl: ttlMs
  };
  
  // Guardar en memoria
  memoryCache.set(key, cacheEntry);
  
  // Guardar en localStorage (persistente)
  try {
    localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
  } catch (e) {
    // Si localStorage está lleno, limpiar caché antigua
    cleanExpiredCache();
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    } catch (e2) {
      logger.warn('No se pudo guardar en localStorage:', e2);
    }
  }
};

/**
 * Obtiene un valor de caché si no ha expirado
 */
export const getCache = (key) => {
  const cacheKey = `${CACHE_PREFIX}${key}`;
  
  // Primero intentar memoria (más rápido)
  const memEntry = memoryCache.get(key);
  if (memEntry && !isExpired(memEntry)) {
    return memEntry.value;
  }
  
  // Luego intentar localStorage
  try {
    const stored = localStorage.getItem(cacheKey);
    if (stored) {
      const entry = JSON.parse(stored);
      if (!isExpired(entry)) {
        // Actualizar memoria
        memoryCache.set(key, entry);
        return entry.value;
      } else {
        // Limpiar expirado
        localStorage.removeItem(cacheKey);
        memoryCache.delete(key);
      }
    }
  } catch (e) {
    logger.warn('Error leyendo caché:', e);
  }
  
  return null;
};

/**
 * Verifica si una entrada de caché ha expirado
 */
const isExpired = (entry) => {
  return Date.now() - entry.timestamp > entry.ttl;
};

/**
 * Limpia todas las entradas de caché expiradas
 */
export const cleanExpiredCache = () => {
  const keysToRemove = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX)) {
      try {
        const entry = JSON.parse(localStorage.getItem(key));
        if (isExpired(entry)) {
          keysToRemove.push(key);
        }
      } catch (e) {
        keysToRemove.push(key);
      }
    }
  }
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
  
  // También limpiar memoria
  memoryCache.forEach((entry, key) => {
    if (isExpired(entry)) {
      memoryCache.delete(key);
    }
  });
};

/**
 * Invalida una entrada de caché específica
 */
export const invalidateCache = (key) => {
  const cacheKey = `${CACHE_PREFIX}${key}`;
  localStorage.removeItem(cacheKey);
  memoryCache.delete(key);
};

/**
 * Invalida todas las entradas de caché que coincidan con un patrón
 */
export const invalidateCachePattern = (pattern) => {
  const keysToRemove = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX) && key.includes(pattern)) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
  
  // También limpiar memoria
  memoryCache.forEach((_, key) => {
    if (key.includes(pattern)) {
      memoryCache.delete(key);
    }
  });
};

/**
 * Obtiene el tamaño total de la caché en localStorage
 */
export const getCacheSize = () => {
  let totalSize = 0;
  let count = 0;
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX)) {
      const value = localStorage.getItem(key);
      totalSize += value ? value.length * 2 : 0; // UTF-16
      count++;
    }
  }
  
  return {
    entries: count,
    sizeBytes: totalSize,
    sizeKB: (totalSize / 1024).toFixed(2)
  };
};

/**
 * Precarga datos del portafolio en caché
 */
export const preloadPortfolioData = async (apiUrl, token) => {
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    // Cargar inversiones
    const investmentsRes = await fetch(`${apiUrl}/user/investments`, { headers });
    if (investmentsRes.ok) {
      const investments = await investmentsRes.json();
      setCache('user_investments', investments, 5 * 60 * 1000); // 5 min
      return investments;
    }
  } catch (e) {
    logger.warn('Error precargando datos:', e);
  }
  return null;
};

/**
 * Cache para precios de stocks con TTL corto
 */
export const cacheStockPrice = (ticker, priceData) => {
  setCache(`price_${ticker}`, priceData, 60 * 1000); // 1 minuto
};

export const getCachedStockPrice = (ticker) => {
  return getCache(`price_${ticker}`);
};

/**
 * Cache para historial de stocks con TTL más largo
 */
export const cacheStockHistory = (ticker, period, interval, data, aggregate = 1) => {
  setCache(`history_${ticker}_${period}_${interval}_${aggregate}`, data, 3 * 60 * 1000); // 3 minutos
};

export const getCachedStockHistory = (ticker, period, interval, aggregate = 1) => {
  return getCache(`history_${ticker}_${period}_${interval}_${aggregate}`);
};

/**
 * Cache para información de stocks
 */
export const cacheStockInfo = (ticker, info) => {
  setCache(`info_${ticker}`, info, 2 * 60 * 1000); // 2 minutos
};

export const getCachedStockInfo = (ticker) => {
  return getCache(`info_${ticker}`);
};

// Limpiar caché expirada al iniciar
cleanExpiredCache();

export default {
  setCache,
  getCache,
  invalidateCache,
  invalidateCachePattern,
  cleanExpiredCache,
  getCacheSize,
  preloadPortfolioData,
  cacheStockPrice,
  getCachedStockPrice,
  cacheStockHistory,
  getCachedStockHistory,
  cacheStockInfo,
  getCachedStockInfo
};
